import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Typography,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EventIcon from "@mui/icons-material/Event";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import StorageFactory from "../../Util/StorageFactory";
import {
  SESSION_USER_COUNTRY_DATA_KEY,
  SESSION_USER_ID_KEY,
  SESSION_USER_TYPE_KEY,
} from "../../Util/VMConstants";
import { isAdminLoggedIn, isEmpty } from "../../Util/UtilHelper";
import {
  assignLicensesTier,
  createOrderTier,
  getSubscriptionDetailsTier,
  listSubscriptionPlansTier,
  verifyPaymentTier,
} from "../../Services/subscriptionTierService";
import {
  ANNUAL_DISCOUNT_PERCENT,
  TIER_ORDER,
  buildCreateOrderTierGstPayload,
  computeAnnualSavingsPercent,
  currencyToCountryKey,
  extractOrderPayload,
  buildRazorpayAutopayOptions,
  getSessionRazorpayPrefill,
  formatTierCurrency,
  formatSubscriptionExpiryDate,
  getDaysUntilSubscriptionExpiry,
  getPriceForCheckout,
  getTierBillingDisplay,
  getTierDisplayMeta,
  getTierDisplayName,
  getTierSubscriptionExpiryDate,
  getTierUserLimitLabel,
  groupPlansByTier,
  isTierDetailsActive,
  loadRazorpayScript,
  resolveTierPlans,
  SUBSCRIPTION_GST_FOOTNOTE,
} from "../../Util/subscriptionTierHelper";
import { align_center, VM_Colors } from "../../Styles/VMStyles";
import { fetchSubscriptionTierStatus } from "../../Redux/slices/subscriptionTierSlice";
import Loader from "../../Components/Loader";
import CheckoutGstConfirmModal from "./CheckoutGstConfirmModal";

function SubscriptionTier() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const sessionFactory = new StorageFactory("session");
  const userID = sessionFactory.get(SESSION_USER_ID_KEY);
  const isAdmin = isAdminLoggedIn(sessionFactory.get(SESSION_USER_TYPE_KEY));
  const sessionCountryData = sessionFactory.get(SESSION_USER_COUNTRY_DATA_KEY) || {};
  const countryKey = currencyToCountryKey(sessionCountryData.currency_code || "INR");

  const [loading, setLoading] = useState(false);
  const [plansLoading, setPlansLoading] = useState(true);
  const [postPurchaseAssigning, setPostPurchaseAssigning] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [plans, setPlans] = useState([]);
  const [billingCycle, setBillingCycle] = useState("Monthly");
  const [payingTier, setPayingTier] = useState("");
  const [gstConfirm, setGstConfirm] = useState(null);
  const [catalogTax, setCatalogTax] = useState(null);

  const groupedPlans = useMemo(() => groupPlansByTier(plans), [plans]);
  const selectedPlanName = billingCycle === "Monthly" ? "Monthly" : "Yearly";

  const annualSavings = useMemo(() => {
    const smallMonthly = groupedPlans[TIER_ORDER[0]]?.find((p) => p.planName === "Monthly");
    const smallYearly = groupedPlans[TIER_ORDER[0]]?.find((p) => p.planName === "Yearly");
    const computed = computeAnnualSavingsPercent(smallMonthly, smallYearly);
    return computed > 0 ? computed : ANNUAL_DISCOUNT_PERCENT;
  }, [groupedPlans]);

  const loadPlans = useCallback(async () => {
    setPlansLoading(true);
    try {
      const resp = await listSubscriptionPlansTier({
        user_id: userID,
        country: countryKey,
      });
      const resolved = resolveTierPlans(
        resp?.status_code === 200 ? resp : null,
        countryKey
      );
      setPlans(resolved);
      setCatalogTax(resp?.status_code === 200 ? resp?.tax || null : null);
    } catch (err) {
      console.error("Failed to load tier plans:", err);
      setPlans(resolveTierPlans(null, countryKey));
      setCatalogTax(null);
    } finally {
      setPlansLoading(false);
    }
  }, [countryKey, userID]);

  const loadSubscriptionDetails = useCallback(async () => {
    if (!userID) return null;
    setLoading(true);
    try {
      const resp = await getSubscriptionDetailsTier(userID);
      setLoading(false);

      if (isTierDetailsActive(resp)) {
        setSubscriptionDetails(resp);
        return resp;
      }

      setSubscriptionDetails(null);
      return resp || null;
    } catch (err) {
      setLoading(false);
      console.error("Error loading tier subscription details:", err);
      setSubscriptionDetails(null);
      return null;
    }
  }, [userID]);

  useEffect(() => {
    if (!isEmpty(userID)) {
      loadPlans();
      loadSubscriptionDetails();
      dispatch(fetchSubscriptionTierStatus(userID));
    }
    window.scrollTo(0, 0);
  }, [userID, loadPlans, loadSubscriptionDetails, dispatch]);

  const currentTier =
    subscriptionDetails?.plan_tier ||
    subscriptionDetails?.tier ||
    subscriptionDetails?.tier_subscription?.plan_tier;
  const currentPlanName =
    subscriptionDetails?.plan_name ||
    subscriptionDetails?.billing_cycle ||
    subscriptionDetails?.tier_subscription?.plan_name;

  const isActiveTier = (tier) => {
    if (!isTierDetailsActive(subscriptionDetails) || !currentTier) return false;
    return String(tier).toLowerCase() === String(currentTier).toLowerCase();
  };

  const getPlanForTier = (tier) => {
    const tierPlans = groupedPlans[tier] || [];
    return (
      tierPlans.find((p) => p.planName === selectedPlanName) ||
      tierPlans[0] ||
      null
    );
  };

  const handleBuyPay = (tier) => {
    if (!userID) {
      toast.error("User not found in session");
      return;
    }
    if (!tier) {
      toast.warn("Select a plan");
      return;
    }

    const prefill = getSessionRazorpayPrefill(sessionFactory);
    if (!prefill.contact) {
      toast.error("Mobile number not found in session. Please log in again.");
      return;
    }

    const planRef = getPlanForTier(tier);
    const breakdown = getPriceForCheckout(planRef, {
      currency: planRef?.currency,
      taxLabel: catalogTax?.label,
      exclusiveNote: catalogTax?.exclusive_note,
    });

    if (!breakdown) {
      toast.error("Unable to calculate tax for this plan");
      return;
    }

    setGstConfirm({
      tier,
      planRef,
      prefill,
      displayName: getTierDisplayName(tier, planRef, countryKey),
      billingLabel: billingCycle === "Monthly" ? "Monthly" : "Annual",
      planPeriod: selectedPlanName,
      currency: breakdown.currency || planRef?.currency || "INR",
      breakdown,
      taxLabel: breakdown.taxLabel || catalogTax?.label,
    });
  };

  const handleCloseGstConfirm = () => {
    setGstConfirm(null);
  };

  const handleConfirmGstPayment = async () => {
    if (!gstConfirm) return;
    const pending = gstConfirm;
    setGstConfirm(null);
    await createOrderAndOpenRazorpay(pending);
  };

  const createOrderAndOpenRazorpay = async ({
    tier,
    planRef,
    prefill,
    breakdown,
  }) => {
    setPayingTier(tier);
    setLoading(true);
    try {
      const orderResponse = await createOrderTier({
        user_id: userID,
        plan_tier: tier,
        plan_name: selectedPlanName,
        country: countryKey,
        contact: prefill.contact,
        name: prefill.name,
        email: prefill.email,
        autopay: true,
        ...buildCreateOrderTierGstPayload(breakdown),
      });
      setLoading(false);

      if (orderResponse?.status_code !== 200) {
        toast.error(orderResponse?.message || "Failed to create order");
        setPayingTier("");
        return;
      }

      const { order, subscriptionId, razorpaySubscriptionId, customerId, checkout, prefill: orderPrefill } =
        extractOrderPayload(orderResponse);
      if (!order?.id && !razorpaySubscriptionId && !checkout?.order_id && !checkout?.subscription_id) {
        toast.error("Invalid order response from server");
        setPayingTier("");
        return;
      }

      await integrateRazorpay(order, subscriptionId, tier, planRef, {
        prefill,
        orderPrefill,
        checkout,
        razorpaySubscriptionId,
        customerId,
        gstBreakdown: breakdown,
      });
    } catch (err) {
      setLoading(false);
      setPayingTier("");
      console.error("Network error", err);
      toast.error("Network or server error");
    }
  };

  const autoAssignLicensesTier = async () => {
    try {
      await assignLicensesTier(userID);
    } catch (err) {
      console.error("assignLicensesTier failed", err);
    }
  };

  const integrateRazorpay = async (
    orderData,
    subscriptionId,
    tier,
    planRef,
    { prefill, orderPrefill, checkout, razorpaySubscriptionId, customerId } = {}
  ) => {
    const isLoaded = await loadRazorpayScript();
    if (!isLoaded) {
      toast.error("Razorpay SDK failed to load. Are you online?");
      setPayingTier("");
      return;
    }

    const sessionPrefill = prefill || getSessionRazorpayPrefill(sessionFactory);
    const resolvedSubscriptionId =
      checkout?.subscription_id || razorpaySubscriptionId || orderData?.subscription_id;
    let paymentSubmitted = false;

    const options = {
      ...buildRazorpayAutopayOptions({
        order: {
          ...orderData,
          subscription_id: resolvedSubscriptionId,
          customer_id: checkout?.customer_id || customerId,
        },
        checkout,
        sessionPrefill,
        orderPrefill,
      }),
      handler: async (response) => {
        paymentSubmitted = true;
        try {
          const payload = {
            user_id: userID,
            subscriptionId,
            razorpay_order_id: response.razorpay_order_id || orderData?.id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_subscription_id:
              response.razorpay_subscription_id || resolvedSubscriptionId,
            razorpay_signature: response.razorpay_signature,
            transaction_status: "success",
            transaction_details: response,
          };

          const verifyData = await verifyPaymentTier(payload);

          if (verifyData.success || verifyData.status_code === 200) {
            toast.success("Payment verified successfully");
            setPostPurchaseAssigning(true);
            try {
              await autoAssignLicensesTier();
              await loadSubscriptionDetails();
              await dispatch(fetchSubscriptionTierStatus({ userID, forceRefresh: true }));
            } finally {
              setPostPurchaseAssigning(false);
              setPayingTier("");
            }
          } else {
            toast.error(verifyData.message || "Payment verification failed");
            setPayingTier("");
          }
        } catch (err) {
          console.error("Error verifying tier payment:", err);
          toast.error("Server error while verifying payment");
          setPayingTier("");
        }
      },
      modal: {
        ...(checkout?.modal || {}),
        ondismiss: () => {
          if (!paymentSubmitted) setPayingTier("");
        },
      },
    };

    const rzp = new window.Razorpay(options);

    rzp.on &&
      rzp.on("payment.failed", async (response) => {
        paymentSubmitted = true;
        toast.error("Payment failed. Please try again.");
        setPayingTier("");
        try {
          await verifyPaymentTier({
            user_id: userID,
            subscriptionId,
            razorpay_order_id:
              response.error?.metadata?.order_id || orderData?.id,
            razorpay_payment_id: response.error?.metadata?.payment_id,
            razorpay_subscription_id:
              response.error?.metadata?.subscription_id || resolvedSubscriptionId,
            razorpay_signature: "",
            transaction_status: "failed",
            transaction_details: response,
          });
        } catch (err) {
          console.error("Failed payment verify:", err);
        }
      });

    rzp.on("payment.cancel", () => setPayingTier(""));

    rzp.open();
  };

  const hasActiveSubscription = isTierDetailsActive(subscriptionDetails);
  const subscriptionExpiryDate = useMemo(
    () => getTierSubscriptionExpiryDate(subscriptionDetails),
    [subscriptionDetails]
  );
  const formattedExpiryDate = formatSubscriptionExpiryDate(subscriptionExpiryDate);
  const daysUntilExpiry = getDaysUntilSubscriptionExpiry(subscriptionExpiryDate);

  if (loading && plansLoading && !plans.length) {
    return <Loader />;
  }

  return (
    <Box sx={{ ml: { xs: 0, lg: 5 }, p: { xs: 1.5, sm: 2 }, pb: 6 }}>
      {(loading || postPurchaseAssigning) && <Loader />}

      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            fontSize: { xs: "1.5rem", sm: "2rem", md: "2.25rem" },
            mb: 1,
            letterSpacing: "-0.02em",
            color: "text.primary",
          }}
        >
          Pricing Plans
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            maxWidth: 520,
            mx: "auto",
            fontSize: { xs: "0.9rem", sm: "1rem" },
            lineHeight: 1.6,
          }}
        >
          Choose the plan that fits your venue. All plans include a 30-day free
          trial.
        </Typography>

        {hasActiveSubscription && (
          <Card
            elevation={0}
            sx={{
              mt: 3,
              mx: "auto",
              maxWidth: 720,
              borderRadius: 3,
              border: `1px solid ${theme.palette.success.light}`,
              bgcolor: "#f3faf5",
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "flex-start", sm: "center" },
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                  <CheckCircleIcon sx={{ color: "success.main", mt: 0.25 }} />
                  <Box sx={{ textAlign: "left" }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase" }}
                    >
                      Active subscription
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                      {getTierDisplayName(currentTier, null, countryKey)}
                      {currentPlanName ? ` · ${currentPlanName}` : ""}
                    </Typography>
                  </Box>
                </Box>

                {formattedExpiryDate && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                      pl: { xs: 4.5, sm: 0 },
                    }}
                  >
                    <EventIcon sx={{ color: "text.secondary", mt: 0.25 }} />
                    <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase" }}
                      >
                        Expires on
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                        {formattedExpiryDate}
                      </Typography>
                      {daysUntilExpiry != null && daysUntilExpiry >= 0 && (
                        <Chip
                          size="small"
                          label={
                            daysUntilExpiry === 0
                              ? "Expires today"
                              : `Expires in ${daysUntilExpiry} day${daysUntilExpiry === 1 ? "" : "s"}`
                          }
                          color={daysUntilExpiry <= 7 ? "warning" : "default"}
                          sx={{ mt: 0.75, fontWeight: 600 }}
                        />
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Billing toggle */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 5 }}>
        <ToggleButtonGroup
          exclusive
          value={billingCycle}
          onChange={(_, value) => value && setBillingCycle(value)}
          sx={{
            bgcolor: VM_Colors.white,
            borderRadius: 999,
            p: 0.5,
            border: "1px solid rgba(15, 23, 42, 0.08)",
            boxShadow: "0 2px 8px rgba(15, 23, 42, 0.04)",
            "& .MuiToggleButton-root": {
              textTransform: "none",
              fontWeight: 600,
              px: { xs: 2.5, sm: 3.5 },
              py: 1,
              border: "none",
              borderRadius: "999px !important",
              color: "text.secondary",
              "&.Mui-selected": {
                bgcolor: VM_Colors.primary,
                color: VM_Colors.white,
                boxShadow: "0 4px 12px rgba(30, 58, 138, 0.25)",
                "&:hover": { bgcolor: VM_Colors.primary },
              },
            },
          }}
        >
          <ToggleButton value="Monthly">Monthly</ToggleButton>
          <ToggleButton value="Yearly">
            Annual — Save {annualSavings}%
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Plan cards — always single row */}
      <Box
        sx={{
          mb: 5,
          overflowX: "auto",
          pt: 1.5,
          "&::-webkit-scrollbar": { height: 6 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#cfd8dc",
            borderRadius: 4,
          },
        }}
      >
        <Grid
          container
          spacing={2.5}
          sx={{
            alignItems: "stretch",
            flexWrap: "nowrap",
            minWidth: { xs: 960, md: "100%" },
          }}
        >
        {TIER_ORDER.map((tier) => {
          const meta = getTierDisplayMeta(tier, countryKey);
          const planRef = getPlanForTier(tier);
          const monthlyPlan = (groupedPlans[tier] || []).find(
            (p) => p.planName === "Monthly"
          );
          const priceDisplay = getTierBillingDisplay({
            billingCycle,
            planRef,
            monthlyPlan,
          });
          const isCurrentPlan = isActiveTier(tier);
          const isPaying = payingTier === tier;
          const showPopular =
            !hasActiveSubscription &&
            (meta.isMostPopular || planRef?.isMostPopular);
          const isHighlighted = isCurrentPlan || showPopular;

          return (
            <Grid item xs={4} key={tier} sx={{ flex: "1 1 33.33%", minWidth: 300 }}>
              <Card
                elevation={0}
                sx={{
                  position: "relative",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 3,
                  overflow: "visible",
                  bgcolor: VM_Colors.white,
                  border: `1px solid ${isHighlighted ? meta.accent : "rgba(15, 23, 42, 0.08)"}`,
                  boxShadow: isHighlighted
                    ? `0 12px 40px ${meta.accent}22`
                    : "0 4px 20px rgba(15, 23, 42, 0.04)",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: `0 16px 40px ${meta.accent}28`,
                  },
                }}
              >
                <Box
                  sx={{
                    height: 4,
                    borderRadius: "12px 12px 0 0",
                    bgcolor: meta.accent,
                  }}
                />

                {showPopular && (
                  <Chip
                    label="Most Popular"
                    size="small"
                    sx={{
                      position: "absolute",
                      top: -12,
                      left: "50%",
                      transform: "translateX(-50%)",
                      bgcolor: meta.accent,
                      color: VM_Colors.white,
                      fontWeight: 700,
                      fontSize: "0.7rem",
                      letterSpacing: 0.4,
                      height: 24,
                      boxShadow: `0 4px 12px ${meta.accent}44`,
                    }}
                  />
                )}

                <CardContent
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    p: { xs: 2.5, sm: 3 },
                    pt: showPopular ? 3.5 : 3,
                    "&:last-child": { pb: 3 },
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "text.primary",
                      mb: 0.75,
                      letterSpacing: "-0.01em",
                      lineHeight: 1.3,
                      fontSize: "1.125rem",
                      ...align_center,
                    }}
                  >
                    {getTierDisplayName(tier, planRef, countryKey)}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 2.5,
                      minHeight: { md: 44 },
                      lineHeight: 1.55,
                      fontSize: "0.875rem",
                    }}
                  >
                    {meta.idealFor}
                  </Typography>

                  <Box sx={{ mb: 2 }}>
                    {priceDisplay ? (
                      <Box>
                        {priceDisplay.listAmount != null &&
                          priceDisplay.listAmount > priceDisplay.amount && (
                            <Typography
                              variant="h3"
                              color="text.secondary"
                              sx={{
                                textDecoration: "line-through",
                                mb: 0.25,
                                fontWeight: 500,
                              }}
                            >
                              {formatTierCurrency(
                                priceDisplay.listAmount,
                                priceDisplay.currency
                              )}
                            </Typography>
                          )}
                        <Box
                          display="flex"
                          alignItems="baseline"
                          gap={0.75}
                          flexWrap="wrap"
                        >
                          <Typography
                            variant="h2"
                            sx={{
                              fontWeight: 800,
                              letterSpacing: "-0.02em",
                              color: "text.primary",
                              // fontSize: { xs: "1.75rem", sm: "2rem" },
                            }}
                          >
                            {formatTierCurrency(
                              priceDisplay.amount,
                              priceDisplay.currency
                            )}
                          </Typography>
                          <Typography
                            variant="h4"
                            color="text.secondary"
                            sx={{ fontWeight: 500 }}
                          >
                            / {priceDisplay.period}
                          </Typography>
                          {priceDisplay.discountPercent > 0 && (
                            <Chip
                              label={`${priceDisplay.discountPercent}% off`}
                              size="small"
                              sx={{
                                height: 22,
                                fontWeight: 700,
                                fontSize: "0.7rem",
                                bgcolor: meta.lightBg,
                                color: meta.accent,
                              }}
                            />
                          )}
                        </Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mt: 1, lineHeight: 1.45 }}
                        >
                          {planRef?.exclusiveNote ||
                            catalogTax?.exclusive_note ||
                            SUBSCRIPTION_GST_FOOTNOTE}
                        </Typography>
                      </Box>
                    ) : null}
                  </Box>

                  <Chip
                    label={getTierUserLimitLabel(planRef || meta)}
                    size="small"
                    sx={{
                      alignSelf: "flex-start",
                      mb: isCurrentPlan && formattedExpiryDate ? 1 : 2.5,
                      bgcolor: meta.lightBg,
                      color: meta.accent,
                      fontWeight: 600,
                      borderRadius: 1.5,
                      height: 28,
                    }}
                  />

                  {isCurrentPlan && formattedExpiryDate && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 2.5,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                      }}
                    >
                      <EventIcon sx={{ fontSize: 16, color: meta.accent }} />
                      {daysUntilExpiry != null && daysUntilExpiry >= 0
                        ? `Expires in ${daysUntilExpiry} day${daysUntilExpiry === 1 ? "" : "s"} · ${formattedExpiryDate}`
                        : `Expires on ${formattedExpiryDate}`}
                    </Typography>
                  )}

                  <Divider sx={{ mb: 2.5, borderColor: "rgba(15, 23, 42, 0.06)" }} />

                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      mb: 1.5,
                      color: "text.primary",
                      fontSize: "0.8rem",
                      letterSpacing: 0.3,
                      textTransform: "uppercase",
                    }}
                  >
                    What&apos;s included
                  </Typography>

                  <Box sx={{ flexGrow: 1, mb: 2.5 }}>
                    {meta.features.map((feature) => (
                      <Box
                        key={feature}
                        display="flex"
                        alignItems="flex-start"
                        gap={1}
                        mb={1.1}
                      >
                        <CheckCircleIcon
                          sx={{ color: meta.accent, fontSize: 17, mt: 0.15, opacity: 0.9 }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary", lineHeight: 1.45 }}
                        >
                          {feature}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {meta.recommendedTeam && (
                    <Box
                      sx={{
                        bgcolor: meta.lightBg,
                        borderRadius: 2,
                        p: 1.75,
                        mb: 2.5,
                        border: `1px solid ${meta.accent}18`,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          display: "block",
                          mb: 0.5,
                          color: meta.accent,
                          letterSpacing: 0.2,
                        }}
                      >
                        {meta.recommendedTeamLabel || "Recommended Team"}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", lineHeight: 1.5 }}
                      >
                        {meta.recommendedTeam}
                      </Typography>
                    </Box>
                  )}

                  {isCurrentPlan ? (
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<CheckCircleIcon />}
                      disabled
                      sx={{
                        textTransform: "none",
                        borderRadius: 2,
                        py: 1.35,
                        borderColor: meta.accent,
                        color: meta.accent,
                        fontWeight: 600,
                      }}
                    >
                      Current plan
                    </Button>
                  ) : isAdmin ? (
                    <Button
                      fullWidth
                      variant={isHighlighted ? "contained" : "outlined"}
                      onClick={() => handleBuyPay(tier)}
                      disabled={loading || postPurchaseAssigning || isPaying || !planRef?.amount}
                      sx={{
                        textTransform: "none",
                        borderRadius: 2,
                        py: 1.35,
                        fontWeight: 600,
                        ...(isHighlighted
                          ? {
                              bgcolor: meta.accent,
                              boxShadow: `0 6px 16px ${meta.accent}33`,
                              "&:hover": {
                                bgcolor: meta.accent,
                                filter: "brightness(0.92)",
                                boxShadow: `0 8px 20px ${meta.accent}40`,
                              },
                            }
                          : {
                              borderColor: meta.accent,
                              color: meta.accent,
                              borderWidth: 1.5,
                              "&:hover": {
                                borderWidth: 1.5,
                                bgcolor: meta.lightBg,
                                borderColor: meta.accent,
                              },
                            }),
                      }}
                    >
                      {isPaying ? "Processing…" : "Subscribe"}
                    </Button>
                  ) : (
                    <Box
                      sx={{
                        bgcolor: meta.lightBg,
                        borderRadius: 2,
                        px: 1.5,
                        py: 1.5,
                        textAlign: "center",
                        border: `1px dashed ${meta.accent}40`,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: meta.accent,
                          fontWeight: 700,
                          lineHeight: 1.4,
                          mb: 0.35,
                        }}
                      >
                        Only an admin can subscribe
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          display: "block",
                          lineHeight: 1.45,
                        }}
                      >
                        Please contact your admin to upgrade this plan.
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      </Box>

      {/* Feature comparison */}
      {/* <Box sx={{ mb: 5 }}>
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, textAlign: "center", mb: 3 }}
        >
          Compare Features
        </Typography>

        <TableContainer
          component={Paper}
          elevation={1}
          sx={{ borderRadius: 3, overflowX: "auto" }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: VM_Colors.white1 }}>
                <TableCell sx={{ fontWeight: 700, minWidth: 180 }}>Feature</TableCell>
                {TIER_ORDER.map((tier) => (
                  <TableCell
                    key={tier}
                    align="center"
                    sx={{ fontWeight: 700, minWidth: 120 }}
                  >
                    {getTierDisplayMeta(tier, countryKey).comparisonLabel}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {COMPARISON_FEATURES.map((row) => (
                <TableRow key={row.name} hover>
                  <TableCell sx={{ fontWeight: 500 }}>{row.name}</TableCell>
                  <TableCell align="center">
                    {row.small ? (
                      <CheckCircleIcon sx={{ color: "#4caf50", fontSize: 20 }} />
                    ) : (
                      <CancelIcon sx={{ color: "#bdbdbd", fontSize: 20 }} />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {row.mid ? (
                      <CheckCircleIcon sx={{ color: "#2196f3", fontSize: 20 }} />
                    ) : (
                      <CancelIcon sx={{ color: "#bdbdbd", fontSize: 20 }} />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {row.enterprise ? (
                      <CheckCircleIcon sx={{ color: "#9c27b0", fontSize: 20 }} />
                    ) : (
                      <CancelIcon sx={{ color: "#bdbdbd", fontSize: 20 }} />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box> */}

      {/* Footer */}
      {/* <Box
        sx={{
          bgcolor: VM_Colors.primary,
          borderRadius: 3,
          py: 4,
          px: 2,
          textAlign: "center",
        }}
      >
        <Typography
          variant="h6"
          sx={{ color: VM_Colors.white, fontWeight: 700, mb: 3 }}
        >
          Trusted by Venue Managers Across India
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: { xs: 2, sm: 3 },
          }}
        >
          {VENUE_TYPE_FOOTER.map((item) => (
            <Box
              key={item.label}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                minWidth: 90,
              }}
            >
              <Typography sx={{ fontSize: "1.75rem", mb: 0.5 }}>
                {item.icon}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.9)", fontWeight: 500 }}
              >
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box> */}
      <CheckoutGstConfirmModal
        open={Boolean(gstConfirm)}
        onClose={handleCloseGstConfirm}
        onConfirm={handleConfirmGstPayment}
        planDisplayName={gstConfirm?.displayName}
        billingLabel={gstConfirm?.billingLabel}
        planPeriod={gstConfirm?.planPeriod}
        breakdown={gstConfirm?.breakdown}
        currency={gstConfirm?.currency}
        taxLabel={gstConfirm?.taxLabel}
      />
    </Box>
  );
}

export default SubscriptionTier;
